import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Alert,
  Link,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AuthApi } from "../../apis";
import { storeSession } from "../../utils/auth";

export default function SignInForm({ onSwitchToSignUp }) {
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string().email("Invalid email address").required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setError(null);
      try {
        const res = await AuthApi.login(values);

        if (res?.data?.status === 200) {
          // Stores the token, the user, the permission keys and the portal in
          // one place — see storeSession.
          storeSession(res.data.data);

          // Where to land comes from the role's portal, not its name: roles
          // are created by admins now, so a name tells us nothing about which
          // shell this session belongs in.
          const portal = res.data.data.portal;
          window.location.href = portal === "admin" ? "/admin" : "/user";
          return;
        }

        console.error("Login failed:", res?.data);
        setError("Login failed. Please check your credentials and try again.");
      } catch (err) {
        console.error("Error occurred:", err);
        // Surface the server's message when there is one — the previous form
        // stored this in state but never rendered it.
        setError(
          err?.response?.data?.message || "An error occurred. Please try again."
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fieldProps = (name) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: Boolean(formik.touched[name] && formik.errors[name]),
    helperText: (formik.touched[name] && formik.errors[name]) || " ",
    fullWidth: true,
    size: "medium",
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TextField
        {...fieldProps("email")}
        type="email"
        label="Email"
        placeholder="you@company.com"
        autoComplete="email"
        autoFocus
      />

      <TextField
        {...fieldProps("password")}
        type={showPassword ? "text" : "password"}
        label="Password"
        placeholder="••••••••"
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword((v) => !v)}
                edge="end"
                size="small"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? (
                  <VisibilityOffIcon sx={{ fontSize: 20 }} />
                ) : (
                  <VisibilityIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2.5, mt: -0.5 }}>
        <Link
          href="#"
          underline="hover"
          variant="body2"
          sx={{ color: "text.secondary", fontWeight: 500 }}
        >
          Forgot your password?
        </Link>
      </Box>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={formik.isSubmitting}
      >
        {formik.isSubmitting ? "Signing in…" : "Sign in"}
      </Button>

      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
        Don&apos;t have an account?{" "}
        <Link
          component="button"
          type="button"
          onClick={onSwitchToSignUp}
          underline="hover"
          sx={{ fontWeight: 600, verticalAlign: "baseline" }}
        >
          Sign up
        </Link>
      </Typography>
    </Box>
  );
}
